import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Observable } from 'rxjs';
import { OnboardingService } from '@app/onboarding/services/onboarding.service';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { TranslateService } from '@ngx-translate/core';
import { TrackerService } from '@app/shared/services/tracker.service';
import { Skill } from '@app/onboarding/onboarding.model';
/**
 * Skills select item component
 * Skills selection list of similar skills and displayed selected skills
 *
 * @param toggleSkill {EventEmitter boolean} emits to the parent that a skill was selected
 *
 * @example
 * ```
 * <dgx-skill-select-items (toggleSkill)="toggleSkill($event)"
 * </dgx-skill-select-items>
 * ```
 */
@Component({
  selector: 'dgx-skill-select-items',
  templateUrl: './skill-select-items.component.html',
  styleUrls: ['./skill-select-items.component.scss'],
})
export class SkillSelectItemsComponent implements OnInit {
  @Input() public showInterestedOnly?: boolean = false;
  @Output()
  public toggleSkill: EventEmitter<boolean> = new EventEmitter<boolean>();
  public displayedSimilarSkills: Observable<any> =
    this.onboardingStateService.displayedSimilarTags$;
  public loadMore: boolean = false;
  public i18n = this.translateService.instant([
    'Onboarding_AdditionalSkillsInterstedIn',
    'Onboarding_LoadMore',
    'Onboarding_AddedSkill',
  ]);

  constructor(
    private onboardingService: OnboardingService,
    private onboardingStateService: OnboardingStateService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private trackerService: TrackerService
  ) {}

  public ngOnInit(): void {
    // Hide the load more button if there are no more similar skills
    this.onboardingStateService.similarTags$.subscribe((similarTags) => {
      this.loadMore = similarTags?.length > 0;
      this.cdr.detectChanges();
    });
  }

  /**
   * Add similar skill to display list
   *
   * @param tag tag to be added
   * @param similarSkills list of similar skills
   */
  public onAddSkill(tag, similarSkills) {
    const onboardingUserAddedTags: Skill[] =
      this.onboardingStateService.onboardingUserAddedTags;
    const profileTags: Skill[] =
      this.onboardingStateService.currentProfileSkills;
    const alltags: Skill[] = [...profileTags, ...onboardingUserAddedTags];
    const index = alltags.findIndex(
      (skill) => skill.name.toLowerCase() === tag.name.toLowerCase()
    );
    // If tag does not exist in display list
    if (index < 0) {
      tag.isFollowing = !tag.isFollowing;
      tag.source = this.i18n.Onboarding_AddedSkill;
      const anySelected = alltags.filter((skill) => skill.isFollowing);

      this.toggleSkill.emit(anySelected.length > 0);
      const similarSkillsIndex = similarSkills.findIndex(
        (skill) => skill.name.toLowerCase() === tag.name.toLowerCase()
      );
      // remove skill from list
      if (similarSkillsIndex > -1) {
        // find the position based on all of the similar skills shown on the page from the ds3 service
        // right now we only get skills once from the ds3 service
        const displayedSimilarSkillIndex =
          this.onboardingStateService.displayedSimilarTags?.findIndex(
            (skill) => skill.name.toLowerCase() === tag.name.toLowerCase()
          );
        this.trackerService.trackEventData({
          action: 'Onboarding Skill Selected',
          category: 'Onboarding',
          properties: {
            Location: 'Onboarding Additional Skills Similarity',
            SkillId: tag.tagId,
            SkillName: tag.name,
            Position: displayedSimilarSkillIndex,
            Skills: this.onboardingStateService.displayedSimilarTags,
            SkillSource: this.onboardingStateService.isSimilarService
              ? 'ds3'
              : 'legacy',
          },
        });
        similarSkills.splice(similarSkillsIndex, 1);
        this.onboardingStateService.updateDisplayedSimilarTags(similarSkills);
        // add more skills if that was the last suggested skill displayed
        if (!similarSkills.length) {
          this.loadMoreSkills();
        }
        this.onboardingService.addUserAddedSkill(tag);
      }
    }
  }

  /**
   * Load more similar skills
   */
  public loadMoreSkills() {
    this.onboardingService.loadMoreSimilarSkills();
    this.trackerService.trackEventData({
      action: 'Onboarding Load More Skills Clicked',
      category: 'Onboarding',
    });
  }
}
